import { create } from "zustand";

import { apiClient, extractErrorMessage, getStoredToken, setStoredToken } from "@/lib/api-client";
import { CurrentUser, School, UserRole } from "@/lib/types";

interface AuthState {
  user: CurrentUser | null;
  school: School | null;
  status: "idle" | "loading" | "authenticated" | "unauthenticated";
  error: string | null;
  isSimulated: boolean;
  simulatedRole: UserRole | null;
  originalAdminToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  guardLogin: (schoolCode: string, email: string, password: string) => Promise<void>;
  consumerSignup: (name: string, email: string, password: string) => Promise<void>;
  simulateRole: (params: {
    school_id?: string | null;
    role: UserRole;
    target_email?: string;
    name?: string;
    duration_minutes?: number;
  }) => Promise<void>;
  exitSimulation: () => Promise<void>;
  logout: () => void;
  hydrate: () => Promise<void>;
}

async function hydrateFromToken(set: (partial: Partial<AuthState>) => void) {
  const me = await apiClient.get("/auth/me");
  set({
    user: me.data.user,
    school: me.data.school,
    status: "authenticated",
    error: null,
  });
}

/**
 * Global reference to the React Query QueryClient.
 * Set by Providers on mount so the auth store can clear cache on logout.
 */
let _queryClientRef: { clear: () => void } | null = null;
export function setQueryClientRef(qc: { clear: () => void }) {
  _queryClientRef = qc;
}

const ORIGINAL_TOKEN_KEY = "pc_super_admin_backup_token";

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  school: null,
  status: "idle",
  error: null,
  isSimulated: typeof window !== "undefined" && !!localStorage.getItem(ORIGINAL_TOKEN_KEY),
  simulatedRole: null,
  originalAdminToken: typeof window !== "undefined" ? localStorage.getItem(ORIGINAL_TOKEN_KEY) : null,

  login: async (email: string, password: string) => {
    set({ status: "loading", error: null });
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      setStoredToken(data.access_token);
      if (typeof window !== "undefined") {
        localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      }
      set({ isSimulated: false, originalAdminToken: null });
      await hydrateFromToken(set);
    } catch (err) {
      setStoredToken(null);
      set({ status: "unauthenticated", error: extractErrorMessage(err, "Invalid email or password") });
      throw err;
    }
  },

  guardLogin: async (schoolCode: string, email: string, password: string) => {
    set({ status: "loading", error: null });
    try {
      const { data } = await apiClient.post("/auth/guard-login", { school_code: schoolCode, email, password });
      setStoredToken(data.access_token);
      if (typeof window !== "undefined") {
        localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      }
      set({ isSimulated: false, originalAdminToken: null });
      await hydrateFromToken(set);
    } catch (err) {
      setStoredToken(null);
      set({ status: "unauthenticated", error: extractErrorMessage(err, "Invalid school code or credentials") });
      throw err;
    }
  },

  consumerSignup: async (name: string, email: string, password: string) => {
    set({ status: "loading", error: null });
    try {
      const { data } = await apiClient.post("/auth/consumer-signup", { name, email, password });
      setStoredToken(data.access_token);
      if (typeof window !== "undefined") {
        localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      }
      set({ isSimulated: false, originalAdminToken: null });
      await hydrateFromToken(set);
    } catch (err) {
      setStoredToken(null);
      set({ status: "unauthenticated", error: extractErrorMessage(err, "Could not create account") });
      throw err;
    }
  },

  simulateRole: async (params) => {
    set({ status: "loading", error: null });
    try {
      // Backup current admin token if not already in simulation
      const currentToken = getStoredToken();
      if (currentToken && !get().isSimulated) {
        if (typeof window !== "undefined") {
          localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);
        }
        set({ originalAdminToken: currentToken });
      }

      const { data } = await apiClient.post("/platform/simulate-token", params);
      setStoredToken(data.access_token);
      set({ isSimulated: true, simulatedRole: data.role });
      await hydrateFromToken(set);
      _queryClientRef?.clear();

      const targetPath = landingPathForRole(data.role as UserRole);
      if (typeof window !== "undefined") {
        window.location.href = targetPath;
      }
    } catch (err) {
      set({ status: "authenticated", error: extractErrorMessage(err, "Failed to initiate role simulation") });
      throw err;
    }
  },

  exitSimulation: async () => {
    set({ status: "loading" });
    const backupToken = get().originalAdminToken || (typeof window !== "undefined" ? localStorage.getItem(ORIGINAL_TOKEN_KEY) : null);
    if (typeof window !== "undefined") {
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    }
    set({ isSimulated: false, simulatedRole: null, originalAdminToken: null });

    if (backupToken) {
      setStoredToken(backupToken);
      _queryClientRef?.clear();
      try {
        await hydrateFromToken(set);
        if (typeof window !== "undefined") {
          window.location.href = "/super-admin";
        }
        return;
      } catch {
        // Fallback to regular logout
      }
    }
    get().logout();
  },

  logout: () => {
    setStoredToken(null);
    if (typeof window !== "undefined") {
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    }
    _queryClientRef?.clear();
    set({ user: null, school: null, status: "unauthenticated", error: null, isSimulated: false, originalAdminToken: null });
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
  },

  hydrate: async () => {
    const token = getStoredToken();
    if (!token) {
      set({ status: "unauthenticated" });
      return;
    }
    set({ status: "loading" });
    try {
      await hydrateFromToken(set);
    } catch {
      setStoredToken(null);
      if (typeof window !== "undefined") {
        localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      }
      set({ status: "unauthenticated", user: null, school: null, isSimulated: false });
    }
  },
}));

export function canManageInventory(role: string | undefined): boolean {
  return (
    role === "SCHOOL_ADMIN" ||
    role === "PRINCIPAL" ||
    role === "VICE_PRINCIPAL" ||
    role === "PE_STAFF" ||
    role === "TEACHER" ||
    role === "STAFF" ||
    role === "STORE_MANAGER"
  );
}

export function isSchoolManagement(role: string | undefined): boolean {
  return (
    role === "SCHOOL_ADMIN" ||
    role === "PRINCIPAL" ||
    role === "VICE_PRINCIPAL" ||
    role === "PE_STAFF" ||
    role === "TEACHER" ||
    role === "STAFF" ||
    role === "STORE_MANAGER"
  );
}

export function roleLabel(role: string): string {
  switch (role) {
    case "SUPER_ADMIN":
      return "Company Super Admin";
    case "SCHOOL_ADMIN":
      return "School Admin";
    case "PRINCIPAL":
      return "Principal / Head of School";
    case "VICE_PRINCIPAL":
      return "Vice Principal";
    case "PE_STAFF":
      return "PE Staff / Sports Coach";
    case "TEACHER":
      return "Teacher";
    case "HEAD_BOY":
      return "Head Boy (Student Leader)";
    case "HEAD_GIRL":
      return "Head Girl (Student Leader)";
    case "STORE_MANAGER":
      return "Store Manager / Custodian";
    case "STAFF":
      return "Staff";
    case "STUDENT":
      return "Student";
    case "PARENT":
      return "Parent";
    case "GUARD":
      return "Security Guard";
    case "CONSUMER":
      return "Community Player";
    default:
      return role;
  }
}

/** Where each role lands right after login. */
export function landingPathForRole(role: UserRole): string {
  switch (role) {
    case "SUPER_ADMIN":
      return "/super-admin";
    case "SCHOOL_ADMIN":
      return "/admin/dashboard";
    case "PRINCIPAL":
    case "VICE_PRINCIPAL":
      return "/portal/principal";
    case "PE_STAFF":
    case "TEACHER":
      return "/portal/teacher";
    case "HEAD_BOY":
    case "HEAD_GIRL":
    case "STUDENT":
      return "/portal/student";
    case "STORE_MANAGER":
      return "/admin/inventory";
    case "STAFF":
      return "/admin/dashboard";
    case "PARENT":
      return "/portal/parent";
    case "GUARD":
      return "/guard";
    case "CONSUMER":
      return "/explore";
    default:
      return "/login";
  }
}
